import { NoPreloading, provideRouter, RouterModule, withComponentInputBinding, withInMemoryScrolling, withPreloading, withRouterConfig } from "@angular/router";
import { BrowserModule } from "@angular/platform-browser";
import { provideAnimationsAsync } from "@angular/platform-browser/animations/async";
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from "@angular/material/form-field";
import { registerLocaleData } from "@angular/common";
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withInterceptorsFromDi } from "@angular/common/http";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { KeycloakInterceptor, KeycloakModule } from "@ogs/ngx-keycloak";
import { IconModule } from "@ogs-gmbh/ngx-m3-components";
import { ServerErrorInterceptor } from "./interceptors/server-error.interceptor";
import { CurrentContextService } from "./services/current-context.service";
import { KEYCLOAK_CONFIG } from "./configs/keycloak.config";
import { EXTJS_CONFIG } from "./configs/extjs.config";
import { HttpService } from "./services/http/http.service";
import { IFRAME_URL } from "./services/extjs/extjs.tokens";
import { CORE_ROUTES_DEFINITION } from "./constants/routes.constant";
import { RouterComponent } from "./components/bootstraps/router.component";
import { HTTP_CONFIG } from "./configs/http-configs/http.config";
import { TRANSLATE_CONFIG } from "./configs/translation/translate.config";
import { TranslationModule } from "@ogs-gmbh/ngx-translate";
import { provideHttpConfig } from "@ogs-gmbh/ngx-http";
import { waitCursorInterceptor } from "@core/interceptors/wait-cursor.interceptor";
import { provideTemplateEngineConfig } from "@ogs-gmbh/ngx-template-engine";
import localeDe from '@angular/common/locales/de';
import localeEn from '@angular/common/locales/en';
import localeFr from '@angular/common/locales/fr';
import { de } from "date-fns/locale";
import { RSQL_OPERATORS } from "./configs/rsql/rsql-operator.config";
import { provideOperatorConfig } from "@ogs-gmbh/rsql";
import { NgModule } from "@angular/core";
import { provideDateFnsAdapter } from "@angular/material-date-fns-adapter";
import { MAT_DATE_LOCALE } from "@angular/material/core";
import { provideToastConfig } from "./types/toast/toast-config.types";
import { TOAST_CONFIG, ToastRegistry } from "./configs/toast/toast.config";
import { provideTableTheme } from "@shared/components/table/types/table-theme.type";
import "@ogs-gmbh/stdx";
import { MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipDefaultOptions } from "@angular/material/tooltip";

// Register locales for Angular i18n (e.g. for date and currency pipes)
registerLocaleData(localeDe, "de");
registerLocaleData(localeEn, "en");
registerLocaleData(localeFr, "fr");
// Provide PLS specific overrides for the rsql operators
provideOperatorConfig(RSQL_OPERATORS);


/* eslint-disable @tseslint/no-extraneous-class */
@NgModule({
  imports: [
    BrowserModule,
    KeycloakModule.forRoot(KEYCLOAK_CONFIG),
    FontAwesomeModule,
    RouterModule,
    IconModule.forRoot({ prefix: "fal" }),
    TranslationModule.forRoot(TRANSLATE_CONFIG)
    /*
     * Need them? :
     * MatListModule,
     * MatIconModule,
     * MatButtonModule,
     * MatDrawerContainer,
     * MatToolbarModule,
     * MatMenuModule,
     * MatExpansionModule,
     * FormsModule,
     * MatFormFieldModule,
     * MatInputModule,
     * CdkFixedSizeVirtualScroll,
     * CdkVirtualForOf,
     * CdkVirtualScrollViewport,
     */
  ],
  declarations: [
    // Bootstraps
    RouterComponent
  ],
  bootstrap: [
    RouterComponent
  ],
  providers: [
    provideRouter(CORE_ROUTES_DEFINITION,
      withInMemoryScrolling({ scrollPositionRestoration: "top" }),
      withRouterConfig({ onSameUrlNavigation: "ignore" }),
      withPreloading(NoPreloading),
      withComponentInputBinding()),
    provideHttpConfig(HTTP_CONFIG),
    {
      provide: IFRAME_URL,
      useValue: EXTJS_CONFIG.url
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ServerErrorInterceptor,
      multi: true
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        appearance: 'outline',
        subscriptSizing: 'dynamic'
      }
    },
    {
      provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
      useValue: {
        showDelay: 400,
        hideDelay: 700
      } satisfies Partial<MatTooltipDefaultOptions>
    },
    HttpService,
    provideAnimationsAsync(),
    { provide: MAT_DATE_LOCALE, useValue: de },
    provideDateFnsAdapter({
      parse: { dateInput: [ 'P', 'PP' ] },
      display: { dateInput: 'P', monthYearLabel: 'MMM yyyy', dateA11yLabel: 'PP', monthYearA11yLabel: 'LLLL yyyy' }
    }),
    provideHttpClient(withInterceptorsFromDi(), withInterceptors([ waitCursorInterceptor ])),
    KeycloakInterceptor,
    CurrentContextService,
    provideToastConfig<ToastRegistry>(TOAST_CONFIG),
    provideTemplateEngineConfig({ fallbackOnError: true }),
    provideTableTheme()
  ]
})
export class CoreModule {}

/* eslint-enable @tseslint/no-extraneous-class */
